export const en = {
    Navigation: {
        "account-settings": {
            label: "Account settings",
            account: {
                label: "Account",
                general: { label: "General" },
                appearance: { label: "Appearance" }
            },
            agent: {
                label: "Agent",
                "agent-preferences": { label: "Agent preferences" },
                skills: { label: "Skills" },
                "skill-detail": { label: "Skill" },
                "connected-apps": { label: "Connected apps" }
            }
        }
    },
    AccountGeneralPage: {
        title: "General",
        description: "General account settings can be managed in Workleap Classic."
    },
    AgentPreferencesPage: {
        description: "Agent preferences can be managed in Workleap AI.",
        goThereNow: "Go there now",
        modalEyebrow: "Heads up",
        modalTitle: "One quick detour",
        modalBody: "We'll take you to Workleap AI to manage your agent preferences. Nothing will be lost.",
        modalCancelLabel: "Cancel",
        modalContinueLabel: "Continue in Workleap AI"
    },
    ConnectedAppsPage: {
        managedInClassic: "Connected apps can be managed in Workleap Classic."
    },
    ConnectedAppsList: {
        description: "Connect, manage, and update the apps you use with Workleap AI, all in one place.",
        totalCount_one: "{{count}} app",
        totalCount_other: "{{count}} apps",
        connectedCount_one: "{{count}} connected",
        connectedCount_other: "{{count}} connected",
        filteredCount_one: "{{count}} app",
        filteredCount_other: "{{count}} apps",
        error: "Couldn't load your connected apps.",
        featureNotEnabledTitle: "Not available for your organization",
        featureNotEnabledDescription: "Connecting personal apps isn't enabled here yet. Reach out to your admin if you need it.",
        policyDisabledTitle: "Personal integrations disabled",
        policyDisabledDescription: "Your organization doesn't allow personal app connections. Reach out to your admin to request access.",
        policyDisabledManagePermissions: "Manage permissions",
        noResults: "No apps match your filters",
        noResultsHint: "Try a different search, or clear a filter.",
        noData: "There are no apps to connect yet.",
        viewToggle: {
            groupLabel: "View mode",
            listView: "List view",
            cardView: "Card view"
        },
        liveRegion: {
            loading: "Loading connected apps"
        }
    },
    ConnectedAppRow: {
        reconnect: "Reconnect",
        openDetail: "{{app}}, {{status}} — view details"
    },
    ConnectedAppCard: {
        reconnect: "Reconnect",
        openDetail: "{{app}}, {{status}} — view details"
    },
    ConnectedAppDetailDialog: {
        unlocks: "What this unlocks",
        dataGathered: "What the agent will read",
        cancel: "Cancel",
        connect: "Connect {{app}}",
        reconnect: "Reconnect {{app}}",
        disconnect: "Disconnect"
    },
    ConnectedAppDetailRoute: {
        disconnectError: "Couldn't disconnect the app. Please try again."
    },
    CustomMcpDetailRoute: {
        disconnectError: "Couldn't disconnect the server. Please try again."
    },
    connectedAppProviders: {
        confluence: {
            description: "Access and search knowledge from Confluence spaces and pages.",
            unlocks: ["Pull decisions and context out of your team's spaces without hunting for the page", "Ground a summary or a report in the documentation you already keep"],
            dataGathered: ["Pages in spaces you can already open", "Comments and mentions on those pages"]
        },
        google: {
            description: "Access your files, emails, and calendar events from Google.",
            unlocks: ["See how your week is really booked, and where the back-to-backs are", "Reference a doc you own or were shared without leaving the conversation", "Catch the commitment buried in an email thread"],
            dataGathered: ["Calendar event titles, attendees, and durations", "Documents in Drive you own or were shared", "Messages in your own Gmail mailbox"],
            privacyNote: "Only what your own Google account can already see. Events you marked private count toward your load, without their details."
        },
        granola: {
            description: "Access notes and transcripts from your Granola meetings.",
            unlocks: ["Turn a meeting you recorded into follow-ups without rewriting your notes", "Reference what was actually said when you prepare the next conversation"],
            dataGathered: ["Notes and transcripts from meetings in your own Granola account"],
            privacyNote: "Only meetings you recorded yourself. Nothing from a teammate's Granola account."
        },
        hubspot: {
            description: "Access contacts, companies, and deals from your HubSpot CRM.",
            unlocks: ["Bring deal and account context into a report without exporting it by hand", "Reference where a customer conversation stands"],
            dataGathered: ["Contacts, companies, and deals your HubSpot user can already open"],
            privacyNote: "Read-only. Nothing in your CRM is created, edited, or deleted."
        },
        jira: {
            description: "Search and explore your work items and projects.",
            unlocks: ["Show what you shipped, with the issues to back it up", "Keep a goal's progress honest against the work actually moving", "See where a sprint stands without opening the board"],
            dataGathered: ["Issues you authored, are assigned, or follow", "Status changes and comments on those issues", "Sprint progress for your projects"],
            privacyNote: "Only projects you can already access."
        },
        microsoftOutlook: {
            description: "Access your emails and calendar events from Microsoft Outlook.",
            unlocks: ["See how your week is really booked, and where the back-to-backs are", "Catch the commitment buried in an email thread", "Prepare a conversation with the context of what preceded it"],
            dataGathered: ["Calendar event titles, attendees, and durations", "Messages in your own mailbox"],
            privacyNote: "Only your own mailbox and calendar. Events you marked private count toward your load, without their details."
        },
        microsoftSharePoint: {
            description: "Access files and pages from your SharePoint sites.",
            unlocks: ["Reference a document from a site you work in without going to find it", "Ground a summary in the files your team actually maintains"],
            dataGathered: ["Files and pages in sites you can already open"],
            privacyNote: "Only sites and files you already have access to."
        },
        microsoftTeams: {
            description: "Access messages and channels from your Microsoft Teams workspace.",
            unlocks: ["Find answers in past conversations without scrolling back through channels", "Bring what a thread settled into a summary or a report"],
            dataGathered: ["Messages in channels you're a member of", "Mentions and replies on your own posts"],
            privacyNote: "Workleap never reads private chats you aren't part of."
        },
        notion: {
            description: "Access pages and databases from your Notion workspace.",
            unlocks: ["Reference your notes and project docs without copying them across", "Ground a summary in the pages you already keep up to date"],
            dataGathered: ["Pages and databases in workspaces you can already open", "Comments and mentions on those pages"]
        },
        slack: {
            description: "Access messages from channels and direct messages you have access to.",
            unlocks: ["Find answers in past conversations without digging through channels", "Bring what a thread settled into a summary or a report", "Surface the recognition your teammates posted about your work"],
            dataGathered: ["Messages in public channels you're a member of", "Direct messages you're part of", "Files and links shared in those conversations"],
            privacyNote: "Workleap never reads DMs you aren't part of, or channels you haven't joined."
        },
        asana: {
            description: "Access tasks and projects from your Asana workspace.",
            unlocks: ["Show what you shipped against the tasks that proved it", "Track where a project stands without opening every board", "See what's blocked and who owns the next step"],
            dataGathered: ["Tasks in projects you're a member of", "Project status and milestones you can already view"]
        },
        clickup: {
            description: "Access tasks and docs from your ClickUp workspace.",
            unlocks: ["Reference the task behind a decision without hunting through spaces", "See where a project stands and what's blocking it"],
            dataGathered: ["Tasks and docs in spaces you can already open"]
        },
        figma: {
            description: "Access files and projects from your Figma workspace.",
            unlocks: ["Reference a design you're working with without leaving the conversation", "Bring design decisions and annotations into a written summary"],
            dataGathered: ["Files and projects in workspaces you can already open"]
        },
        github: {
            description: "Access repositories and issues from your GitHub account.",
            unlocks: ["Show what you shipped with the pull requests and commits that prove it", "See where a project stands and what's open for review", "Bring issue context into a report without switching tabs"],
            dataGathered: ["Repositories, issues, and pull requests you can already access"],
            privacyNote: "Only repositories your GitHub account can already see."
        },
        gong: {
            description: "Access calls and conversations from your Gong workspace.",
            unlocks: ["Bring what was said on a call into a follow-up or a summary", "Reference deal context from the conversations that shaped it"],
            dataGathered: ["Calls and transcripts you can already access in your Gong workspace"],
            privacyNote: "Only calls shared with or recorded by your Gong user."
        },
        intercom: {
            description: "Access conversations and contacts from your Intercom workspace.",
            unlocks: ["Bring customer conversation context into a report or a handoff", "Reference where a support conversation stands without switching tabs"],
            dataGathered: ["Conversations and contacts your Intercom user can already open"],
            privacyNote: "Read-only. Nothing in Intercom is created, edited, or deleted."
        },
        linear: {
            description: "Access issues and projects from your Linear workspace.",
            unlocks: ["Show what shipped with the issues that back it up", "See where a cycle stands without opening the board", "Keep a roadmap honest against what's actually moving"],
            dataGathered: ["Issues you authored, are assigned, or follow", "Project and cycle progress for your teams"]
        },
        miro: {
            description: "Access boards and content from your Miro workspace.",
            unlocks: ["Reference a board you worked on without describing it from memory", "Bring visual decisions and diagrams into a written summary"],
            dataGathered: ["Boards in workspaces you can already open"]
        },
        mixpanel: {
            description: "Access product analytics and reports from your Mixpanel project.",
            unlocks: ["Pull funnel and retention data into a report without exporting it by hand", "Ground a product decision in the metrics that matter"],
            dataGathered: ["Events, reports, and dashboards in projects you can already access"],
            privacyNote: "Read-only. No data is modified in Mixpanel."
        }
    },
    ConnectedAppsToolbar: {
        searchPlaceholder: "Search apps",
        searchAriaLabel: "Search connected apps",
        filters: {
            status: "Status",
            statusSelected: "{{count}} statuses",
            statusAriaLabel: "Filter by status",
            clearFilter: "Clear filter",
            categoriesAriaLabel: "Filter by category"
        },
        resetFilters: "Reset filters"
    },
    connectedAppCategories: {
        communication: "Communication",
        "calendar-and-meetings": "Calendar & meetings",
        documents: "Documents",
        "project-management": "Project management",
        "engineering-and-analytics": "Engineering & analytics",
        design: "Design",
        "sales-and-support": "Sales & support",
        "custom-server": "Custom server"
    },
    connectedAppStatuses: {
        connected: "Connected",
        expired: "Connection expired",
        missingScopes: "Missing permissions",
        notConnected: "Not connected"
    },
    Errors: {
        retry: "Try again",
        reassurance: "Please try again later.",
        refreshBanner: "Couldn't refresh — showing the last results."
    },
    SkillsPage: {
        description: "Shape how the agent runs each skill for you.",
        liveRegion: {
            loading: "Loading skills"
        },
        features: {
            meetings: { label: "1:1s" },
            engagement: { label: "Engagement" },
            recognition: { label: "Good Vibes" },
            performance_reviews: { label: "Performance reviews" },
            people: { label: "Employee journey" },
            plans: { label: "Plans" }
        }
    },
    agentSkills: {
        skills: {
            wov_engagement_report: {
                name: "Pulse report",
                description: "Sends a weekly summary of how your team's engagement is trending."
            },
            wov_help_me_reply_to_feedback: {
                name: "Help me reply to feedback",
                description: "Drafts a thoughtful response to employee feedback."
            },
            wpm_review: {
                name: "Performance review",
                description: "Gathers the evidence for a review, then drafts, rewrites, and sharpens the answers in your voice."
            },
            wai_people_about: {
                name: "About People",
                description: "Writes a short, sourced profile of a person, scoped to what the viewer can see."
            },
            draft_a_plan: {
                name: "Draft a plan",
                description: "Builds the plan a moment calls for: onboarding, off-boarding, return from leave, improvement, growth, or succession."
            },
            wov_summarize_feedback: {
                name: "Summarize feedback",
                description: "Highlights the themes in your feedback, explains why they matter, and suggests concrete actions."
            },
            wov_feedback_export: {
                name: "Export feedback report",
                description: "Turns your feedback report into a presentation or document ready to share."
            },
            wov_analyze_engagement: {
                name: "Analyze engagement",
                description: "Highlights what stands out in your engagement, eNPS, and participation results, explains why, and suggests next steps."
            },
            wov_export_engagement: {
                name: "Export engagement report",
                description: "Turns your engagement report into a presentation or document ready to share."
            }
        }
    },
    SkillDetailPage: {
        personalize: {
            title: "Personalize",
            description: "How the agent behaves when it uses this skill for you.",
            byDefault: "Skill templates",
            byDefaultDescription: "Each one is a different set of instructions the agent can follow. Anything you add is yours alone."
        },
        liveRegion: {
            loading: "Loading skill"
        }
    },
    SkillTemplates: {
        empty: "No templates yet. Create one to add another way of running this skill.",
        emptyReadOnly: "No templates yet.",
        creationDisabled: "Your organization hasn't enabled personal templates for this skill.",
        newTemplate: "New template",
        newTemplateName: "New template",
        defaultLabel: "Default",
        toggleCard: "Toggle {{name}}",
        scopeWorkleap: "Workleap",
        scopeOrganization: "Your organization",
        scopeUser: "Yours",
        showAll_one: "Show {{count}} more template",
        showAll_other: "Show {{count}} more templates",
        showFewer: "Show fewer",
        standardTemplate: "Standard template",
        standardTemplates: {
            wov_engagement_report: {
                name: "Standard pulse report"
            },
            wov_help_me_reply_to_feedback: {
                name: "Standard feedback reply"
            },
            wpm_review: {
                name: "Balanced and in our voice"
            }
        }
    },
    SkillResourcePanel: {
        loading: "Loading template",
        error: "Couldn't load this template.",
        retry: "Try again"
    },
    SkillTemplateEditor: {
        nameLabel: "Name",
        namePlaceholder: "Name this template",
        requiredName: "Enter a template name.",
        nameTooLong: "Keep the name under {{count}} characters.",
        contentLabel: "Agent instructions",
        contentDescription: "How the agent should run this skill when it uses this template.",
        contentPlaceholder: "Write the guidance the agent should follow, in plain language.",
        requiredContent: "Enter template instructions.",
        contentTooLong: "Keep the instructions under {{count}} characters.",
        cancelNew: "Cancel",
        discard: "Discard",
        create: "Create template",
        saveChanges: "Save changes",
        delete: "Delete",
        cancel: "Cancel",
        deleteTitle: "Delete this template?",
        deleteBody: "It will stop being available to the agent. This can't be undone.",
        createdToast: "Template created.",
        createErrorToast: "Couldn't create the template.",
        savedToast: "Template saved.",
        saveErrorToast: "Couldn't save the template.",
        deletedToast: "Template deleted.",
        deleteErrorToast: "Couldn't delete the template."
    },
    AppearancePage: {
        display: {
            title: "Display",
            description: "Theme and accent color used across Workleap on this device"
        }
    },
    ColorSchemeField: {
        label: "Theme",
        options: {
            light: "Light",
            system: "System",
            dark: "Dark"
        }
    },
    AccentColorField: {
        label: "Accent color",
        themes: {
            "workleap-bleu": "Workleap Bleu",
            "screen-green": "Screen Green",
            "wednesday-pink": "Wednesday Pink",
            "group-chat-lilac": "Group Chat Lilac",
            "mono-neutral": "Mono Neutral"
        }
    }
};
